/**
 * Rails API Client
 *
 * Type-safe HTTP client with automatic snake_case ↔ camelCase conversion.
 * Designed for Rails + Inertia.js + Svelte/React projects.
 */
import { toCamelCase, toSnakeCase } from './case-converter.js';
// ─── Client ───────────────────────────────────────────────
export function createApiClient(config = {}) {
    const { baseUrl = '', csrfSelector = 'meta[name="csrf-token"]', excludeKeys, onError, headers: defaultHeaders = {}, } = config;
    const excluded = excludeKeys ? new Set(excludeKeys) : undefined;
    function generateRequestId() {
        if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
            return globalThis.crypto.randomUUID();
        }
        return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }
    function getCsrfToken() {
        if (typeof document === 'undefined')
            return null;
        return document.querySelector(csrfSelector)?.getAttribute('content') ?? null;
    }
    async function request(endpoint, options = {}) {
        const { params, skipCaseConversion = false, ...fetchOptions } = options;
        // Build URL with query params
        let url = `${baseUrl}${endpoint}`;
        if (params) {
            const searchParams = new URLSearchParams();
            const converted = skipCaseConversion ? params : toSnakeCase(params, excluded);
            for (const [key, value] of Object.entries(converted)) {
                if (value !== undefined)
                    searchParams.append(key, String(value));
            }
            const qs = searchParams.toString();
            if (qs)
                url += `?${qs}`;
        }
        // Convert request body
        let body;
        if (fetchOptions.body !== undefined && fetchOptions.body !== null) {
            const data = skipCaseConversion ? fetchOptions.body : toSnakeCase(fetchOptions.body, excluded);
            body = JSON.stringify(data);
        }
        const requestId = generateRequestId();
        const csrfToken = getCsrfToken();
        try {
            const response = await fetch(url, {
                ...fetchOptions,
                body,
                headers: {
                    'Content-Type': 'application/json',
                    'X-Request-ID': requestId,
                    ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
                    ...defaultHeaders,
                    ...fetchOptions.headers,
                },
            });
            if (!response.ok) {
                let apiError = { message: `HTTP ${response.status}`, status: response.status, requestId };
                try {
                    const raw = await response.json();
                    const converted = skipCaseConversion ? raw : toCamelCase(raw, excluded);
                    apiError = {
                        status: converted.status ?? response.status,
                        code: converted.code,
                        message: converted.message ?? `HTTP ${response.status}`,
                        debugMessage: converted.debugMessage,
                        errors: converted.errors,
                        requestId: converted.requestId ?? response.headers.get('X-Request-ID') ?? requestId,
                    };
                }
                catch { /* ignore parse errors */ }
                onError?.(apiError);
                return { success: false, ...apiError };
            }
            const text = await response.text();
            if (!text)
                return { success: true };
            const raw = JSON.parse(text);
            const data = skipCaseConversion ? raw : toCamelCase(raw, excluded);
            // If response is an object with data properties, spread them
            if (data && typeof data === 'object' && !Array.isArray(data)) {
                return { success: true, ...data };
            }
            return { success: true, data };
        }
        catch (error) {
            const message = error instanceof Error ? error.message : 'Network error';
            const apiError = { message, requestId };
            onError?.(apiError);
            return { success: false, ...apiError };
        }
    }
    return {
        request,
        get(endpoint, options) {
            return request(endpoint, { ...options, method: 'GET' });
        },
        post(endpoint, body, options) {
            return request(endpoint, { ...options, method: 'POST', body });
        },
        patch(endpoint, body, options) {
            return request(endpoint, { ...options, method: 'PATCH', body });
        },
        put(endpoint, body, options) {
            return request(endpoint, { ...options, method: 'PUT', body });
        },
        delete(endpoint, options) {
            return request(endpoint, { ...options, method: 'DELETE' });
        },
    };
}
/** Pre-configured default client (no baseUrl, standard CSRF) */
export const api = createApiClient();
//# sourceMappingURL=client.js.map